import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, X, Save, Building2 } from 'lucide-react';
import DashboardHeader from './DashboardHeader';

const API = process.env.REACT_APP_BACKEND_API_URL;

const emptyForm = { full_name: '', short_name: '' };

const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '4px', display: 'block' };
const fieldWrapStyle = { display: 'flex', flexDirection: 'column' };

const CompanyDetails = ({ user, onLogout }) => {
    const navigate = useNavigate();

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // SPOC list for the company currently being added/edited. For an
    // existing company, each add/rename/delete hits the backend immediately
    // (like the KPI grid's per-row save); for a brand-new company, SPOCs are
    // staged locally and submitted together with the company itself.
    const [spocs, setSpocs] = useState([]); // { id: number|null, spoc_name: string }
    const [newSpocName, setNewSpocName] = useState('');
    const [spocError, setSpocError] = useState('');
    const [editingSpocId, setEditingSpocId] = useState(null);
    const [editingSpocName, setEditingSpocName] = useState('');

    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchCompanies = useCallback(() => {
        setLoading(true);
        fetch(`${API}/api/companies`)
            .then(res => res.json())
            .then(data => setCompanies(data))
            .catch(err => console.error('Failed to fetch companies:', err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    const openAddForm = () => {
        setEditingId(null);
        setForm(emptyForm);
        setSpocs([]);
        setNewSpocName('');
        setSpocError('');
        setError('');
        setShowForm(true);
    };

    const openEditForm = (company) => {
        setEditingId(company.id);
        // Existing data is pulled straight in — every SPOC already on record
        // for this company shows up immediately, ready to edit.
        setSpocs((company.spocs || []).map(s => ({ id: s.id, spoc_name: s.spoc_name })));
        setNewSpocName('');
        setSpocError('');
        setEditingSpocId(null);
        setForm({ full_name: company.full_name, short_name: company.short_name });
        setError('');
        setShowForm(true);
    };

    const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.full_name.trim() || !form.short_name.trim()) {
            setError('Company Full Name and Company Short Name are required.');
            return;
        }

        setSaving(true);
        try {
            const url = editingId ? `${API}/api/companies/${editingId}` : `${API}/api/companies`;
            const method = editingId ? 'PATCH' : 'POST';
            const body = editingId
                ? { ...form, requester_role: user?.role }
                : { ...form, spocs: spocs.map(s => s.spoc_name), requester_role: user?.role };
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Failed to save company.');
                return;
            }

            setShowForm(false);
            setForm(emptyForm);
            setEditingId(null);
            setSpocs([]);
            fetchCompanies();
        } catch (err) {
            console.error('Failed to save company:', err);
            setError('Server connection failed.');
        } finally {
            setSaving(false);
        }
    };

    // Existing company: SPOC changes save immediately, one action at a time.
    const handleAddSpoc = async () => {
        const name = newSpocName.trim();
        if (!name) return;
        setSpocError('');

        if (!editingId) {
            // New company being drafted — stage locally, submitted on Save.
            if (spocs.some(s => s.spoc_name.toLowerCase() === name.toLowerCase())) {
                setSpocError('That SPOC is already in the list.');
                return;
            }
            setSpocs(prev => [...prev, { id: null, spoc_name: name }]);
            setNewSpocName('');
            return;
        }

        try {
            const response = await fetch(`${API}/api/companies/${editingId}/spocs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ spoc_name: name, requester_role: user?.role })
            });
            const data = await response.json();
            if (!response.ok) {
                setSpocError(data.error || 'Failed to add SPOC.');
                return;
            }
            setSpocs(prev => [...prev, { id: data.id, spoc_name: data.spoc_name }]);
            setNewSpocName('');
        } catch (err) {
            console.error('Failed to add SPOC:', err);
            setSpocError('Server connection failed.');
        }
    };

    const handleStartRenameSpoc = (spoc) => {
        setEditingSpocId(spoc.id ?? `local-${spoc.spoc_name}`);
        setEditingSpocName(spoc.spoc_name);
    };

    const handleSaveRenameSpoc = async (spoc) => {
        const name = editingSpocName.trim();
        if (!name) return;
        setSpocError('');

        if (!editingId || spoc.id === null) {
            // Local (unsaved company or unsaved SPOC) — just update in place.
            setSpocs(prev => prev.map(s => s === spoc ? { ...s, spoc_name: name } : s));
            setEditingSpocId(null);
            return;
        }

        try {
            const response = await fetch(`${API}/api/companies/${editingId}/spocs/${spoc.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ spoc_name: name, requester_role: user?.role })
            });
            const data = await response.json();
            if (!response.ok) {
                setSpocError(data.error || 'Failed to rename SPOC.');
                return;
            }
            setSpocs(prev => prev.map(s => s.id === spoc.id ? { ...s, spoc_name: data.spoc_name } : s));
            setEditingSpocId(null);
        } catch (err) {
            console.error('Failed to rename SPOC:', err);
            setSpocError('Server connection failed.');
        }
    };

    const handleDeleteSpoc = async (spoc) => {
        setSpocError('');
        if (!editingId || spoc.id === null) {
            setSpocs(prev => prev.filter(s => s !== spoc));
            return;
        }
        try {
            const response = await fetch(`${API}/api/companies/${editingId}/spocs/${spoc.id}?requester_role=${encodeURIComponent(user?.role || '')}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (!response.ok) {
                setSpocError(data.error || 'Failed to remove SPOC.');
                return;
            }
            setSpocs(prev => prev.filter(s => s.id !== spoc.id));
        } catch (err) {
            console.error('Failed to remove SPOC:', err);
            setSpocError('Server connection failed.');
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        setDeleting(true);
        try {
            const response = await fetch(`${API}/api/companies/${deleteConfirm.id}?requester_role=${encodeURIComponent(user?.role || '')}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (!response.ok) {
                setError(data.error || 'Failed to delete company.');
                setDeleteConfirm(null);
                return;
            }
            setDeleteConfirm(null);
            fetchCompanies();
        } catch (err) {
            console.error('Failed to delete company:', err);
            setError('Server connection failed.');
        } finally {
            setDeleting(false);
        }
    };

    const filteredCompanies = companies.filter(c => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (c.full_name || '').toLowerCase().includes(q) ||
            (c.short_name || '').toLowerCase().includes(q) ||
            (c.spoc_name || '').toLowerCase().includes(q);
    });

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Company Details" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button
                    onClick={() => navigate('/admin')}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Building2 size={20} /> Company Details
                </h2>
            </div>

            <div style={{ background: '#eff6ff', color: '#1e40af', padding: '10px 14px', borderRadius: '8px', fontSize: '12px', marginBottom: '16px' }}>
                This is the single source of truth for company data across Recruiter360. Updating a company's Short Name or Full Name here automatically updates it everywhere it's used — Job Gathering, Tasks, Candidates, Interviews, and Analytics.
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '20px', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                        Companies ({filteredCompanies.length})
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search companies..."
                            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '220px' }}
                        />
                        <button
                            onClick={openAddForm}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#2563eb', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                        >
                            <Plus size={14} /> Add Company
                        </button>
                    </div>
                </div>

                {error && !showForm && !deleteConfirm && (
                    <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '14px' }}>{error}</div>
                )}

                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ padding: '10px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                                {['Company Full Name', 'Company Short Name', 'Company SPOC Name(s)', 'Edit', 'Delete'].map(h => (
                                    <th key={h} style={{ padding: '10px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCompanies.map((c, snoIdx) => (
                                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px', color: '#64748b', fontWeight: '600' }}>{snoIdx + 1}</td>
                                    <td style={{ padding: '10px' }}>{c.full_name}</td>
                                    <td style={{ padding: '10px', fontWeight: '700', color: '#2563eb' }}>{c.short_name}</td>
                                    <td style={{ padding: '10px' }}>
                                        {(c.spocs && c.spocs.length > 0) ? c.spocs.map(s => s.spoc_name).join(', ') : '-'}
                                    </td>
                                    <td style={{ padding: '10px' }}>
                                        <button
                                            onClick={() => openEditForm(c)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', color: '#334155', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                        >
                                            <Pencil size={12} /> Edit
                                        </button>
                                    </td>
                                    <td style={{ padding: '10px' }}>
                                        <button
                                            onClick={() => { setDeleteConfirm(c); setError(''); }}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                        >
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filteredCompanies.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                                        {companies.length === 0 ? 'No companies yet. Click "Add Company" to create the first one.' : 'No companies match your search.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {showForm && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)', maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>
                                {editingId ? 'Edit Company' : 'Add Company'}
                            </h3>
                            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div style={fieldWrapStyle}>
                                <label style={labelStyle}>Company Full Name *</label>
                                <input style={inputStyle} value={form.full_name} onChange={handleChange('full_name')} placeholder="e.g. Elite Techno Solutions Private Limited" required />
                            </div>
                            <div style={fieldWrapStyle}>
                                <label style={labelStyle}>Company Short Name *</label>
                                <input style={inputStyle} value={form.short_name} onChange={handleChange('short_name')} placeholder="e.g. Elite Techno" required />
                                <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>Displayed everywhere else in the app by default.</span>
                            </div>
                            <div style={fieldWrapStyle}>
                                <label style={labelStyle}>Company SPOC Name(s)</label>
                                <span style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>
                                    A company can have multiple SPOCs — each one saves as soon as you add, rename, or remove it.
                                </span>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                                    {spocs.map((spoc) => {
                                        const isThisEditing = editingSpocId === (spoc.id ?? `local-${spoc.spoc_name}`);
                                        return (
                                            <div key={spoc.id ?? `local-${spoc.spoc_name}`} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                {isThisEditing ? (
                                                    <>
                                                        <input
                                                            style={{ ...inputStyle, flex: 1 }}
                                                            value={editingSpocName}
                                                            onChange={(e) => setEditingSpocName(e.target.value)}
                                                            autoFocus
                                                        />
                                                        <button type="button" onClick={() => handleSaveRenameSpoc(spoc)} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '8px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                                                            Save
                                                        </button>
                                                        <button type="button" onClick={() => setEditingSpocId(null)} style={{ background: '#f1f5f9', color: '#334155', border: 'none', padding: '8px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span style={{ flex: 1, padding: '8px 10px', background: '#f8fafc', borderRadius: '6px', fontSize: '13px', border: '1px solid #e2e8f0' }}>
                                                            {spoc.spoc_name}
                                                        </span>
                                                        <button type="button" onClick={() => handleStartRenameSpoc(spoc)} style={{ background: '#f1f5f9', color: '#334155', border: 'none', padding: '8px 9px', borderRadius: '6px', cursor: 'pointer' }}>
                                                            <Pencil size={12} />
                                                        </button>
                                                        <button type="button" onClick={() => handleDeleteSpoc(spoc)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '8px 9px', borderRadius: '6px', cursor: 'pointer' }}>
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {spocs.length === 0 && (
                                        <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>No SPOCs added yet.</span>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                        style={{ ...inputStyle, flex: 1 }}
                                        value={newSpocName}
                                        onChange={(e) => setNewSpocName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSpoc(); } }}
                                        placeholder="e.g. Rahul Sharma"
                                    />
                                    <button type="button" onClick={handleAddSpoc} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#2563eb', color: '#fff', border: 'none', padding: '0 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                                        <Plus size={12} /> Add
                                    </button>
                                </div>
                                {spocError && (
                                    <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', marginTop: '6px' }}>{spocError}</div>
                                )}
                            </div>

                            {error && (
                                <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', fontSize: '12px' }}>{error}</div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#2563eb', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
                                >
                                    <Save size={14} /> {saving ? 'Saving...' : editingId ? 'Update Company' : 'Add Company'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Delete Company?</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                            This will permanently delete <strong>{deleteConfirm.short_name}</strong> ({deleteConfirm.full_name}). This cannot be undone.
                        </p>
                        {error && (
                            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>{error}</div>
                        )}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => { setDeleteConfirm(null); setError(''); }}
                                disabled={deleting}
                                style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDetails;